'use client';

import { useState, useMemo, memo, useCallback } from 'react';
import { 
  X, 
  Search, 
  Terminal as TerminalIcon,
  Activity,
  Zap,
  Globe,
  RefreshCw,
  TrendingUp,
  LineChart,
  BarChart3,
  Layers
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import MarketChart from '@/components/MarketChart';
import LiveMarketData from '@/components/LiveMarketData';
import GlassCard from '@/components/GlassCard';
import GlassButton from '@/components/GlassButton';
import LiquidGlassContainer from '@/components/LiquidGlassContainer';

interface WatchlistItem {
  symbol: string;
  name: string;
  category: 'stocks' | 'crypto' | 'forex' | 'commodities';
}

// Institutional Grade Asset List (Curated Top 50 of each for maximum market coverage)
const INSTITUTIONAL_WATCHLIST: WatchlistItem[] = [
  // STOCKS (50) - Top Volume Leaders
  { symbol: 'AAPL', name: 'Apple Inc.', category: 'stocks' },
  { symbol: 'NVDA', name: 'Nvidia Corp.', category: 'stocks' },
  { symbol: 'TSLA', name: 'Tesla, Inc.', category: 'stocks' },
  { symbol: 'AMZN', name: 'Amazon.com Inc.', category: 'stocks' },
  { symbol: 'MSFT', name: 'Microsoft Corp.', category: 'stocks' },
  { symbol: 'AMD', name: 'Advanced Micro Devices', category: 'stocks' },
  { symbol: 'META', name: 'Meta Platforms Inc.', category: 'stocks' },
  { symbol: 'GOOGL', name: 'Alphabet Inc.', category: 'stocks' },
  { symbol: 'PLTR', name: 'Palantir Technologies', category: 'stocks' },
  { symbol: 'NFLX', name: 'Netflix, Inc.', category: 'stocks' },
  { symbol: 'AVGO', name: 'Broadcom Inc.', category: 'stocks' },
  { symbol: 'MU', name: 'Micron Technology', category: 'stocks' },
  { symbol: 'COIN', name: 'Coinbase Global', category: 'stocks' },
  { symbol: 'MSTR', name: 'MicroStrategy Inc.', category: 'stocks' },
  { symbol: 'JPM', name: 'JPMorgan Chase & Co.', category: 'stocks' },
  { symbol: 'BAC', name: 'Bank of America', category: 'stocks' },
  { symbol: 'COST', name: 'Costco Wholesale', category: 'stocks' },
  { symbol: 'INTC', name: 'Intel Corporation', category: 'stocks' },
  { symbol: 'PYPL', name: 'PayPal Holdings', category: 'stocks' },
  { symbol: 'DIS', name: 'Walt Disney Co.', category: 'stocks' },
  { symbol: 'ADBE', name: 'Adobe Inc.', category: 'stocks' },
  { symbol: 'CRM', name: 'Salesforce, Inc.', category: 'stocks' },
  { symbol: 'WMT', name: 'Walmart Inc.', category: 'stocks' },
  { symbol: 'V', name: 'Visa Inc.', category: 'stocks' },
  { symbol: 'MA', name: 'Mastercard Inc.', category: 'stocks' },
  { symbol: 'XOM', name: 'Exxon Mobil Corp.', category: 'stocks' },
  { symbol: 'CVX', name: 'Chevron Corp.', category: 'stocks' },
  { symbol: 'ORCL', name: 'Oracle Corp.', category: 'stocks' },
  { symbol: 'CSCO', name: 'Cisco Systems', category: 'stocks' },
  { symbol: 'NKE', name: 'Nike, Inc.', category: 'stocks' },
  { symbol: 'PFE', name: 'Pfizer Inc.', category: 'stocks' },
  { symbol: 'MRK', name: 'Merck & Co.', category: 'stocks' },
  { symbol: 'PEP', name: 'PepsiCo, Inc.', category: 'stocks' },
  { symbol: 'KO', name: 'Coca-Cola Co.', category: 'stocks' },
  { symbol: 'ABBV', name: 'AbbVie Inc.', category: 'stocks' },
  { symbol: 'TMO', name: 'Thermo Fisher Scientific', category: 'stocks' },
  { symbol: 'LLY', name: 'Eli Lilly & Co.', category: 'stocks' },
  { symbol: 'MCD', name: 'McDonald\'s Corp.', category: 'stocks' },
  { symbol: 'WFC', name: 'Wells Fargo & Co.', category: 'stocks' },
  { symbol: 'CAT', name: 'Caterpillar Inc.', category: 'stocks' },
  { symbol: 'HD', name: 'Home Depot, Inc.', category: 'stocks' },
  { symbol: 'IBM', name: 'IBM Corporation', category: 'stocks' },
  { symbol: 'T', name: 'AT&T Inc.', category: 'stocks' },
  { symbol: 'VZ', name: 'Verizon Communications', category: 'stocks' },
  { symbol: 'GS', name: 'Goldman Sachs Group', category: 'stocks' },
  { symbol: 'MS', name: 'Morgan Stanley', category: 'stocks' },
  { symbol: 'BA', name: 'Boeing Company', category: 'stocks' },
  { symbol: 'GE', name: 'GE Aerospace', category: 'stocks' },
  { symbol: 'HON', name: 'Honeywell International', category: 'stocks' },
  { symbol: 'MMM', name: '3M Company', category: 'stocks' },

  // CRYPTO (50) - Highest Market Caps & Volume Nodes
  { symbol: 'BTC', name: 'Bitcoin', category: 'crypto' },
  { symbol: 'ETH', name: 'Ethereum', category: 'crypto' },
  { symbol: 'SOL', name: 'Solana', category: 'crypto' },
  { symbol: 'BNB', name: 'Binance Coin', category: 'crypto' },
  { symbol: 'XRP', name: 'Ripple', category: 'crypto' },
  { symbol: 'ADA', name: 'Cardano', category: 'crypto' },
  { symbol: 'DOGE', name: 'Dogecoin', category: 'crypto' },
  { symbol: 'TRX', name: 'TRON', category: 'crypto' },
  { symbol: 'DOT', name: 'Polkadot', category: 'crypto' },
  { symbol: 'LINK', name: 'Chainlink', category: 'crypto' },
  { symbol: 'MATIC', name: 'Polygon', category: 'crypto' },
  { symbol: 'LTC', name: 'Litecoin', category: 'crypto' },
  { symbol: 'SHIB', name: 'Shiba Inu', category: 'crypto' },
  { symbol: 'DAI', name: 'Dai Stablecoin', category: 'crypto' },
  { symbol: 'BCH', name: 'Bitcoin Cash', category: 'crypto' },
  { symbol: 'UNI', name: 'Uniswap', category: 'crypto' },
  { symbol: 'LEO', name: 'UNUS SED LEO', category: 'crypto' },
  { symbol: 'NEAR', name: 'NEAR Protocol', category: 'crypto' },
  { symbol: 'AVAX', name: 'Avalanche', category: 'crypto' },
  { symbol: 'KAS', name: 'Kaspa', category: 'crypto' },
  { symbol: 'XMR', name: 'Monero', category: 'crypto' },
  { symbol: 'ATOM', name: 'Cosmos', category: 'crypto' },
  { symbol: 'ETC', name: 'Ethereum Classic', category: 'crypto' },
  { symbol: 'XLM', name: 'Stellar', category: 'crypto' },
  { symbol: 'ICP', name: 'Internet Computer', category: 'crypto' },
  { symbol: 'STX', name: 'Stacks', category: 'crypto' },
  { symbol: 'FIL', name: 'Filecoin', category: 'crypto' },
  { symbol: 'LDO', name: 'Lido DAO', category: 'crypto' },
  { symbol: 'HBAR', name: 'Hedera', category: 'crypto' },
  { symbol: 'INJ', name: 'Injective', category: 'crypto' },
  { symbol: 'RNDR', name: 'Render', category: 'crypto' },
  { symbol: 'OP', name: 'Optimism', category: 'crypto' },
  { symbol: 'AR', name: 'Arweave', category: 'crypto' },
  { symbol: 'IMX', name: 'Immutable', category: 'crypto' },
  { symbol: 'TIA', name: 'Celestia', category: 'crypto' },
  { symbol: 'APT', name: 'Aptos', category: 'crypto' },
  { symbol: 'MKR', name: 'Maker', category: 'crypto' },
  { symbol: 'GRT', name: 'The Graph', category: 'crypto' },
  { symbol: 'THETA', name: 'Theta Network', category: 'crypto' },
  { symbol: 'QNT', name: 'Quant', category: 'crypto' },
  { symbol: 'RUNE', name: 'THORChain', category: 'crypto' },
  { symbol: 'AAVE', name: 'Aave', category: 'crypto' },
  { symbol: 'FLOW', name: 'Flow', category: 'crypto' },
  { symbol: 'SNX', name: 'Synthetix', category: 'crypto' },
  { symbol: 'EGLD', name: 'MultiversX', category: 'crypto' },
  { symbol: 'SAND', name: 'The Sandbox', category: 'crypto' },
  { symbol: 'MANA', name: 'Decentraland', category: 'crypto' },
  { symbol: 'AXS', name: 'Axie Infinity', category: 'crypto' },
  { symbol: 'ALGO', name: 'Algorand', category: 'crypto' },
  { symbol: 'HNT', name: 'Helium', category: 'crypto' },

  // FOREX (50) - All Majors & Liquid Minors
  { symbol: 'EUR/USD', name: 'Euro / US Dollar', category: 'forex' },
  { symbol: 'USD/JPY', name: 'US Dollar / Japanese Yen', category: 'forex' },
  { symbol: 'GBP/USD', name: 'British Pound / US Dollar', category: 'forex' },
  { symbol: 'AUD/USD', name: 'Australian Dollar / US Dollar', category: 'forex' },
  { symbol: 'USD/CAD', name: 'US Dollar / Canadian Dollar', category: 'forex' },
  { symbol: 'USD/CHF', name: 'US Dollar / Swiss Franc', category: 'forex' },
  { symbol: 'NZD/USD', name: 'New Zealand Dollar / US Dollar', category: 'forex' },
  { symbol: 'EUR/JPY', name: 'Euro / Japanese Yen', category: 'forex' },
  { symbol: 'GBP/JPY', name: 'British Pound / Japanese Yen', category: 'forex' },
  { symbol: 'EUR/GBP', name: 'Euro / British Pound', category: 'forex' },
  { symbol: 'AUD/JPY', name: 'Australian Dollar / Japanese Yen', category: 'forex' },
  { symbol: 'USD/CNY', name: 'US Dollar / Chinese Yuan', category: 'forex' },
  { symbol: 'EUR/CHF', name: 'Euro / Swiss Franc', category: 'forex' },
  { symbol: 'CAD/JPY', name: 'Canadian Dollar / Japanese Yen', category: 'forex' },
  { symbol: 'AUD/CAD', name: 'Australian Dollar / Canadian Dollar', category: 'forex' },
  { symbol: 'CHF/JPY', name: 'Swiss Franc / Japanese Yen', category: 'forex' },
  { symbol: 'EUR/CAD', name: 'Euro / Canadian Dollar', category: 'forex' },
  { symbol: 'GBP/CAD', name: 'British Pound / Canadian Dollar', category: 'forex' },
  { symbol: 'AUD/NZD', name: 'Australian Dollar / New Zealand Dollar', category: 'forex' },
  { symbol: 'EUR/AUD', name: 'Euro / Australian Dollar', category: 'forex' },
  { symbol: 'GBP/AUD', name: 'British Pound / Australian Dollar', category: 'forex' },
  { symbol: 'USD/HKD', name: 'US Dollar / Hong Kong Dollar', category: 'forex' },
  { symbol: 'USD/SGD', name: 'US Dollar / Singapore Dollar', category: 'forex' },
  { symbol: 'USD/MXN', name: 'US Dollar / Mexican Peso', category: 'forex' },
  { symbol: 'USD/INR', name: 'US Dollar / Indian Rupee', category: 'forex' },
  { symbol: 'USD/TRY', name: 'US Dollar / Turkish Lira', category: 'forex' },
  { symbol: 'USD/ZAR', name: 'US Dollar / South African Rand', category: 'forex' },
  { symbol: 'EUR/SEK', name: 'Euro / Swedish Krona', category: 'forex' },
  { symbol: 'EUR/NOK', name: 'Euro / Norwegian Krone', category: 'forex' },
  { symbol: 'USD/DKK', name: 'US Dollar / Danish Krone', category: 'forex' },
  { symbol: 'SGD/JPY', name: 'Singapore Dollar / Japanese Yen', category: 'forex' },
  { symbol: 'CAD/CHF', name: 'Canadian Dollar / Swiss Franc', category: 'forex' },
  { symbol: 'AUD/CHF', name: 'Australian Dollar / Swiss Franc', category: 'forex' },
  { symbol: 'NZD/JPY', name: 'New Zealand Dollar / Japanese Yen', category: 'forex' },
  { symbol: 'GBP/CHF', name: 'British Pound / Swiss Franc', category: 'forex' },
  { symbol: 'EUR/NZD', name: 'Euro / New Zealand Dollar', category: 'forex' },
  { symbol: 'NZD/CAD', name: 'New Zealand Dollar / Canadian Dollar', category: 'forex' },
  { symbol: 'USD/RUB', name: 'US Dollar / Russian Ruble', category: 'forex' },
  { symbol: 'USD/THB', name: 'US Dollar / Thai Baht', category: 'forex' },
  { symbol: 'USD/PLN', name: 'US Dollar / Polish Zloty', category: 'forex' },
  { symbol: 'USD/ILS', name: 'US Dollar / Israeli Shekel', category: 'forex' },
  { symbol: 'USD/KRW', name: 'US Dollar / Korean Won', category: 'forex' },
  { symbol: 'USD/BRL', name: 'US Dollar / Brazilian Real', category: 'forex' },
  { symbol: 'USD/CLP', name: 'US Dollar / Chilean Peso', category: 'forex' },
  { symbol: 'USD/PHP', name: 'US Dollar / Philippine Peso', category: 'forex' },
  { symbol: 'USD/SAR', name: 'US Dollar / Saudi Riyal', category: 'forex' },
  { symbol: 'USD/AED', name: 'US Dollar / UAE Dirham', category: 'forex' },
  { symbol: 'EUR/TRY', name: 'Euro / Turkish Lira', category: 'forex' },
  { symbol: 'EUR/HUF', name: 'Euro / Hungarian Forint', category: 'forex' },
  { symbol: 'GBP/NZD', name: 'British Pound / New Zealand Dollar', category: 'forex' },

  // COMMODITIES (10 Core)
  { symbol: 'GOLD', name: 'Gold Spot', category: 'commodities' },
  { symbol: 'SILVER', name: 'Silver Spot', category: 'commodities' },
  { symbol: 'CL', name: 'Crude Oil', category: 'commodities' },
  { symbol: 'NG', name: 'Natural Gas', category: 'commodities' },
  { symbol: 'HG', name: 'Copper', category: 'commodities' },
  { symbol: 'GC', name: 'Gold Futures', category: 'commodities' },
  { symbol: 'SI', name: 'Silver Futures', category: 'commodities' },
  { symbol: 'PL', name: 'Platinum', category: 'commodities' },
  { symbol: 'PA', name: 'Palladium', category: 'commodities' },
  { symbol: 'ZW', name: 'Wheat', category: 'commodities' },
];

export default function MarketWatchPage() {
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'stocks' | 'crypto' | 'forex' | 'commodities'>('crypto');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);

  // Optimized Filtered Watchlist
  const filteredWatchlist = useMemo(() => {
    return INSTITUTIONAL_WATCHLIST.filter(item => {
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      const matchesSearch = item.symbol.toLowerCase().includes(searchQuery.toLowerCase()) || 
                           item.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  // Extract symbols for telemetry subscription (Limit to 50 for performance)
  const gridSymbols = useMemo(() => {
    return filteredWatchlist.slice(0, 50).map(item => item.symbol);
  }, [filteredWatchlist]);

  // Selection Handler
  const handleSelectAsset = useCallback((symbol: string) => {
    setSelectedSymbol(symbol);
  }, []);

  return (
    <LiquidGlassContainer className="min-h-screen bg-bg-primary pb-20 selection:bg-brand-teal/30">
      {/* Institutional Intelligence Header */}
      <nav className="border-b border-white/5 bg-bg-primary/40 backdrop-blur-3xl sticky top-20 z-40">
        <div className="max-w-[1800px] mx-auto px-8 h-24 flex items-center justify-between">
          <div className="flex items-center gap-10">
            <div className="flex flex-col">
              <h1 className="text-2xl font-black text-white tracking-[0.2em] uppercase italic leading-none">
                Neural <span className="text-brand-teal-light">Pulse</span>
              </h1>
              <span className="text-[10px] font-bold text-text-tertiary uppercase tracking-widest mt-1 opacity-50">Virtual Brokerage Gateway</span>
            </div>
            
            <div className="hidden lg:flex items-center gap-4 px-6 py-2.5 bg-white/5 rounded-2xl border border-white/10 shadow-inner group">
              <div className="relative">
                <div className="w-2 h-2 bg-brand-teal rounded-full animate-pulse shadow-glow" />
                <div className="absolute inset-0 w-2 h-2 bg-brand-teal rounded-full animate-ping opacity-60" />
              </div>
              <div className="flex flex-col">
                 <span className="text-[9px] font-black uppercase tracking-widest text-brand-teal-light">Network Synchronized</span>
                 <span className="text-[8px] font-bold text-text-tertiary uppercase">Latency: 24ms</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-6 flex-1 max-w-xl mx-12">
            <div className="relative w-full group">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-text-tertiary group-focus-within:text-brand-teal-light transition-colors" size={18} />
              <input
                type="text"
                placeholder="Locate Neural Asset Node..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-14 pr-6 py-3.5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-teal/50 transition-all font-bold text-sm tracking-tight"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
             <GlassButton variant="ghost" className="w-12 h-12 p-0 rounded-2xl border-white/10">
                <RefreshCw size={20} className="text-text-tertiary" />
             </GlassButton>
             <div className="h-10 w-px bg-white/10 mx-2" />
             <div className="hidden xl:flex flex-col text-right">
                <span className="text-[10px] font-black text-white tracking-widest">GATEWAY ACTIVE</span>
                <span className="text-[8px] font-bold text-brand-teal-light tracking-tighter uppercase opacity-70">Secured via Neural Node</span>
             </div>
          </div>
        </div>
      </nav>

      <div className="max-w-[1800px] mx-auto px-8 py-10">
        
        {/* Universal Category Selector */}
        <div className="mb-12 flex flex-wrap items-center gap-3">
          {["all", "stocks", "crypto", "forex", "commodities"].map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat as any)}
              className={`px-10 py-4 rounded-2xl text-[11px] font-black uppercase tracking-[0.2em] transition-all duration-500 border ${
                selectedCategory === cat 
                ? 'bg-brand-teal text-bg-primary border-brand-teal shadow-[0_0_30px_rgba(0,255,222,0.3)] scale-105' 
                : 'bg-white/5 text-text-tertiary border-white/10 hover:bg-white/10 hover:text-white'
              }`}
            >
              {cat === 'stocks' ? 'Equities' : cat === 'crypto' ? 'Digital' : cat === 'forex' ? 'Currency' : cat === 'commodities' ? 'Hard' : 'Universal'}
            </button>
          ))}
          
          <div className="ml-auto flex items-center gap-3 px-6 py-4 bg-brand-teal/5 rounded-2xl border border-brand-teal/10">
             <Layers size={14} className="text-brand-teal-light" />
             <span className="text-[10px] font-black uppercase tracking-widest text-text-tertiary">
               Mapping: <span className="text-white">{filteredWatchlist.length}</span> Active Nodes Identified
             </span>
          </div>
        </div>

        {/* PRIMARY ASSET GRID - TOP HIERARCHY */}
        <section className="relative z-10 transition-all duration-1000">
          <div className="absolute -inset-10 bg-gradient-to-b from-brand-teal/5 via-transparent to-transparent blur-[100px] opacity-40 pointer-events-none" />
          <LiveMarketData 
            symbols={gridSymbols}
            onSelectAsset={handleSelectAsset}
          />
        </section>

        {/* Institutional Bottom HUD */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <GlassCard variant="subtle" className="p-10 border-white/5 bg-[#121212] rounded-3xl">
            <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-text-tertiary mb-6 opacity-40">Gateway Status</h4>
            <div className="flex flex-col gap-2">
              <span className="text-white font-black text-3xl tracking-tighter italic">ACTIVE</span>
              <span className="text-[9px] font-bold text-brand-teal-light uppercase tracking-widest opacity-60">Institutional Node Online</span>
            </div>
          </GlassCard>

          <GlassCard variant="subtle" className="p-10 border-white/5 bg-[#121212] rounded-3xl">
             <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-text-tertiary mb-6 opacity-40">Index Feed: S&P 500</h4>
             <div className="text-white font-black text-3xl tracking-tighter tabular-nums flex items-baseline gap-3">
                4,567.89 <span className="text-[11px] text-brand-teal-light font-bold">+23.45 (0.52%)</span>
             </div>
          </GlassCard>

          <GlassCard variant="subtle" className="p-10 border-white/5 bg-[#121212] rounded-3xl">
             <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-text-tertiary mb-6 opacity-40">Nasdaq Composite</h4>
             <div className="text-white font-black text-3xl tracking-tighter tabular-nums flex items-baseline gap-3">
                14,234.56 <span className="text-[11px] text-brand-teal-light font-bold">+41.67 (0.33%)</span>
             </div>
          </GlassCard>

          <GlassCard variant="subtle" className="p-10 border-white/5 bg-[#121212] rounded-3xl">
             <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-text-tertiary mb-6 opacity-40">Dow Industrials</h4>
             <div className="text-white font-black text-3xl tracking-tighter tabular-nums flex items-baseline gap-3">
                34,567.89 <span className="text-[11px] text-brand-plum-light font-bold">-15.34 (0.04%)</span>
             </div>
          </GlassCard>
        </div>
      </div>

      {/* CENTRALIZED NEURAL INSPECTION POPUP (THE TRADE VIEW / INDICATOR POPUP) */}
      <AnimatePresence>
        {selectedSymbol && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 md:p-12 lg:p-20">
             {/* Backdrop with High-Fidelity Blur */}
             <motion.div 
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               exit={{ opacity: 0 }}
               onClick={() => setSelectedSymbol(null)}
               className="absolute inset-0 bg-bg-primary/90 backdrop-blur-2xl"
             />

             {/* Modal Container */}
             <motion.div 
               initial={{ scale: 0.95, opacity: 0, y: 30 }}
               animate={{ scale: 1, opacity: 1, y: 0 }}
               exit={{ scale: 0.95, opacity: 0, y: 30 }}
               transition={{ type: "spring", damping: 30, stiffness: 400 }}
               className="relative w-full max-w-[1500px] h-full max-h-[900px] bg-bg-secondary/30 rounded-[3.5rem] border border-white/10 shadow-[0_0_150px_rgba(0,255,222,0.15)] overflow-hidden flex flex-col"
             >
                {/* Institutional Header */}
                <header className="flex justify-between items-center px-10 h-24 border-b border-white/5 bg-white/5 backdrop-blur-md">
                   <div className="flex items-center gap-8">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-4">
                           <span className="text-3xl font-black text-white tracking-tighter italic uppercase">{selectedSymbol}</span>
                           <div className="px-3 py-1 bg-brand-teal/20 rounded-lg border border-brand-teal/30">
                              <span className="text-[10px] font-black text-brand-teal-light uppercase">Neural Verified</span>
                           </div>
                        </div>
                        <span className="text-[10px] font-bold text-text-tertiary uppercase tracking-widest mt-1">High-Fidelity Telemetry Node</span>
                      </div>
                   </div>

                   <div className="flex items-center gap-8">
                      <div className="hidden md:flex items-center gap-3">
                         <div className="flex flex-col text-right">
                            <span className="text-[10px] font-black text-white uppercase tracking-widest">Institutional Consensus</span>
                            <span className="text-[9px] font-bold text-brand-teal-light uppercase">92% Neural Confidence</span>
                         </div>
                         <div className="h-10 w-px bg-white/10 mx-2" />
                      </div>

                      <button 
                        onClick={() => setSelectedSymbol(null)}
                        className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/5 hover:bg-brand-plum/20 hover:text-brand-plum-light transition-all border border-white/10 focus:outline-none focus:ring-2 focus:ring-brand-plum/50"
                      >
                         <X size={28} />
                      </button>
                   </div>
                </header>

                <main className="flex-1 flex flex-col xl:row">
                   {/* Primary Chart Visualization Zone */}
                   <div className="flex-1 bg-black/40 relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-brand-teal/5 to-transparent pointer-events-none" />
                      <MarketChart symbol={selectedSymbol} height={900 - 96 - 160} />
                   </div>

                   {/* Execution & Signal Control Node (Right Sidebar) */}
                   <aside className="w-full xl:w-[380px] border-l border-white/5 bg-white/[0.03] p-10 flex flex-col justify-between">
                      <div className="space-y-12">
                         <section>
                            <h5 className="text-[11px] font-black uppercase tracking-[0.2em] text-text-tertiary mb-6 opacity-60">Neural Pulse Metrics</h5>
                            <div className="space-y-5">
                               {[
                                 { label: 'Volatility Index', val: 'Low-Sovereign', color: 'text-brand-teal-light' },
                                 { label: 'Neural Sentiment', val: 'Strong Accumulation', color: 'text-brand-teal-light' },
                                 { label: 'Market Correlation', val: '0.84 Delta', color: 'text-white' },
                                 { label: 'Execution Priority', val: 'Tier 1 Alpha', color: 'text-amber-400' }
                               ].map((metric, i) => (
                                 <div key={i} className="flex justify-between items-center py-1.5 border-b border-white/5">
                                    <span className="text-[10px] font-black text-text-tertiary uppercase tracking-tight">{metric.label}</span>
                                    <span className={`text-[11px] font-mono font-bold uppercase ${metric.color}`}>{metric.val}</span>
                                 </div>
                               ))}
                            </div>
                         </section>

                         <section className="bg-brand-teal/5 p-6 rounded-[2rem] border border-brand-teal/10 relative overflow-hidden group">
                            <div className="absolute top-2 right-2 opacity-20 group-hover:opacity-40 transition-opacity">
                               <TerminalIcon size={16} className="text-brand-teal" />
                            </div>
                            <h5 className="text-[10px] font-black uppercase tracking-widest text-text-tertiary mb-4">A2A Narrative Node</h5>
                            <p className="text-[11px] leading-relaxed font-bold text-text-secondary uppercase italic">
                              "Asset node identified in fractal expansion zone. Neural links confirm institutional liquidity influx. Recommend scaling long positions on verified retracements."
                            </p>
                         </section>
                      </div>

                      <div className="space-y-4 pt-12 border-t border-white/10">
                         <GlassButton asChild variant="primary" className="w-full h-20 rounded-[2rem] font-black uppercase tracking-[0.2em] text-sm italic shadow-glow">
                            <Link to={`/trading?symbol=${selectedSymbol}&side=BUY`}>Execute Deployment</Link>
                         </GlassButton>
                         <GlassButton asChild variant="ghost" className="w-full h-20 rounded-[2rem] font-black uppercase tracking-[0.2em] text-sm italic border-brand-plum/20 text-brand-plum-light hover:bg-brand-plum/10">
                            <Link to={`/trading?symbol=${selectedSymbol}&side=SELL`}>Initiate Liquidation</Link>
                         </GlassButton>
                      </div>
                   </aside>
                </main>
             </motion.div>
          </div>
        )}
      </AnimatePresence>
    </LiquidGlassContainer>
  );
}
